import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Save, ArrowLeft, Loader2, Clock, GraduationCap,
  FileText, Settings, BarChart3, BookOpen, UserCheck, Copy, Archive, Trash2,
  Plus, Building2, ChevronRight,
  Pencil, UserPlus, Briefcase,
  Check, FolderOpen,
  FolderPlus, Search, Upload, Share2, Home, Eye, Layers,
  QrCode, Image, Info, X, Download, MoreVertical, Mail
} from 'lucide-react';
import { toast } from 'sonner';
import SessionAvancementTab from '@/components/admin/SessionAvancementTab';
import { SessionConfigTab } from '@/components/admin/SessionConfigTab';
import { SessionGestionTab } from '@/components/admin/SessionGestionTab';
import { SessionEspaceApprenantTab } from '@/components/admin/SessionEspaceApprenantTab';
import { SessionSuiviTab } from '@/components/admin/SessionSuiviTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Primary tabs (stepper) ───
const PRIMARY_TABS = [
  { id: 'avancement', label: 'Avancement', icon: BarChart3, color: 'from-indigo-600 to-purple-600', ring: 'ring-indigo-200 dark:ring-indigo-800', bg: 'bg-indigo-100 dark:bg-indigo-900/40', text: 'text-indigo-600 dark:text-indigo-400' },
  { id: 'configuration', label: 'Configuration', icon: Settings, color: 'from-purple-500 to-indigo-500', ring: 'ring-purple-200 dark:ring-purple-800', bg: 'bg-purple-100 dark:bg-purple-900/40', text: 'text-purple-600 dark:text-purple-400' },
  { id: 'gestion', label: 'Gestion', icon: FileText, color: 'from-emerald-500 to-teal-500', ring: 'ring-emerald-200 dark:ring-emerald-800', bg: 'bg-emerald-100 dark:bg-emerald-900/40', text: 'text-emerald-600 dark:text-emerald-400' },
  { id: 'espace_apprenant', label: 'Espace Apprenant', icon: GraduationCap, color: 'from-cyan-400 to-teal-400', ring: 'ring-cyan-200 dark:ring-cyan-800', bg: 'bg-cyan-100 dark:bg-cyan-900/40', text: 'text-cyan-600 dark:text-cyan-400' },
  { id: 'suivi', label: 'Suivi', icon: UserCheck, color: 'from-amber-400 to-orange-400', ring: 'ring-amber-200 dark:ring-amber-800', bg: 'bg-amber-100 dark:bg-amber-900/40', text: 'text-amber-600 dark:text-amber-400' },
];

const EMPTY_CRENEAU = { date: '', start_time: '09:00', end_time: '12:00' };

const SessionCreatePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { sessionId } = useParams();
  const programmeId = searchParams.get('programme');

  const isEditMode = !!sessionId;

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEditMode || !!programmeId);
  const [programme, setProgramme] = useState(null);
  const [primaryTab, setPrimaryTab] = useState('configuration');

  // Invoice modal
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceClient, setInvoiceClient] = useState(null);
  const [facturesList, setFacturesList] = useState([]);

  // Data lists for dropdowns
  const [intervenantsList, setIntervenantsList] = useState([]);
  const [apprenantsList, setApprenantsList] = useState([]);
  const [lieuxList, setLieuxList] = useState([]);

  // Documents & E-learning state (backend-driven)
  const [docFolders, setDocFolders] = useState([]);
  const [docFiles, setDocFiles] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState('default');
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareDocId, setShareDocId] = useState(null);
  const [shareSearch, setShareSearch] = useState('');
  const [shareWith, setShareWith] = useState('all');
  const [shareUsers, setShareUsers] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [elSequences, setElSequences] = useState([]);
  const [elParcours, setElParcours] = useState([]);
  const [elSelectedParcours, setElSelectedParcours] = useState('');
  const [showSequenceEditor, setShowSequenceEditor] = useState(null);
  const [showAddSequence, setShowAddSequence] = useState(false);

  const [form, setForm] = useState({
    title: '',
    type_session: 'formation_professionnelle',
    gestionnaire_1: '',
    gestionnaire_2: '',
    inter_entreprise: false,
    fuseau_horaire: 'UTC +01:00 Europe/Paris',
    code_interne: '',
    sous_traitance: false,
    type_action_formation: 'action_formation',
    specialite_formation: '',
    diplome_vise: 'aucun',
    nom_titre_vise: '',
    formation_a_distance: false,
    lieu_formation: '',
    lieu_id: '',
    start_date: '',
    end_date: '',
    start_time: '09:00',
    end_time: '17:00',
    creneaux: [],
    pricing_enterprise: 0,
    pricing_individual: 0,
    pricing_independent: 0,
    type: 'presentiel',
    location: '',
    trainer: '',
    max_capacity: 15,
    status: 'upcoming',
    programme_id: programmeId || '',
    course_title: '',
    description: '',
    pedagogical_objectives: '',
    learner_profile: '',
    training_content: '',
    execution_monitoring: '',
    certification_modalities: '',
    apprenants: [],
    intervenants: [],
    modules: [],
    clients: [],
    // Gestion
    conventions: [],
    convocations_auto_send: false,
    convocations_days_before: 7,
    convocations_doc_template: 'Nouveau modèle',
    convocations_email_template: 'Email de convocation',
    convocations: [],
    evaluations: { preformation: [], a_chaud: [], a_froid: [], managers: [], intervenants: [] },
    factures: [],
    // Espace Apprenant
    elearning_enabled: true,
    elearning_open_date: '',
    elearning_close_date: '',
    elearning_sequences: [],
    elearning_parcours: [],
    learner_documents: [],
    learner_folders: [{ folder_id: 'default', name: '(sans dossier)', documents: [] }, { folder_id: 'supports', name: 'Supports de la formation et des activités', documents: [] }],
    learner_access_password: '',
    learner_welcome_message: 'Bienvenue dans votre espace de formation. Vous trouverez ici tous les contenus et ressources nécessaires à votre parcours.',
    learner_session_display_name: '',
    learner_cover_image: '',
    learner_session_description: '',
    learner_messaging_enabled: false,
    learner_extranet_enabled: true,
    learner_show_lieu: true,
    learner_show_carte: true,
    learner_show_programme: true,
    learner_show_suivi_pedagogique: true,
    learner_show_planning: true,
    learner_show_apprenants: false,
    learner_show_reglement: true,
    learner_show_progress: true,
    learner_show_certificates: true,
    learner_show_emargements: true,
    learner_show_evaluations: true,
    learner_show_documents: true,
    learner_show_co_learners: true,
    learner_show_badges: true,
  });

  const update = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  }, []);

  // Fetch invoices for this session
  const fetchFactures = useCallback(async () => {
    if (!sessionId) return;
    try {
      const res = await fetch(`${API}/admin/sessions/${sessionId}/factures`, { credentials: 'include' });
      if (res.ok) setFacturesList(await res.json());
    } catch (e) { /* silent */ }
  }, [sessionId]);

  const deleteFacture = async (factureId) => {
    if (!window.confirm('Supprimer cette facture ?')) return;
    try {
      const res = await fetch(`${API}/admin/sessions/${sessionId}/factures/${factureId}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) {
        toast.success('Facture supprimée');
        fetchFactures();
      }
    } catch (e) { toast.error('Erreur de suppression'); }
  };

  const openInvoiceModal = (client) => {
    setInvoiceClient(client);
    setShowInvoiceModal(true);
  };

  // Load dropdown data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [intRes, appRes, lieuxRes] = await Promise.all([
          fetch(`${API}/intervenants`, { credentials: 'include' }),
          fetch(`${API}/apprenants`, { credentials: 'include' }),
          fetch(`${API}/lieux`, { credentials: 'include' }),
        ]);
        if (intRes.ok) setIntervenantsList(await intRes.json());
        if (appRes.ok) setApprenantsList(await appRes.json());
        if (lieuxRes.ok) setLieuxList(await lieuxRes.json());
      } catch (e) { /* silent */ }
    };
    fetchData();
  }, []);

  // Load session data in edit mode OR programme data for pre-fill
  useEffect(() => {
    const load = async () => {
      try {
        if (isEditMode) {
          const res = await fetch(`${API}/admin/sessions/${sessionId}`, { credentials: 'include' });
          if (res.ok) {
            const data = await res.json();
            setForm(prev => ({ ...prev, ...data }));
            if (data.programme_id) {
              const pRes = await fetch(`${API}/programmes/${data.programme_id}`, { credentials: 'include' });
              if (pRes.ok) setProgramme(await pRes.json());
            }
          }
        } else if (programmeId) {
          const pRes = await fetch(`${API}/programmes/${programmeId}`, { credentials: 'include' });
          if (pRes.ok) {
            const prog = await pRes.json();
            setProgramme(prog);
            setForm(prev => ({
              ...prev,
              title: `Session — ${prog.title}`,
              course_title: prog.title,
              programme_id: programmeId,
              type: prog.modalities?.toLowerCase().includes('distanciel') ? 'distanciel'
                : prog.modalities?.toLowerCase().includes('mixte') ? 'mixte' : 'presentiel',
              description: prog.description || '',
              pedagogical_objectives: prog.pedagogical_objectives || '',
              learner_profile: prog.learner_profile || '',
              training_content: prog.training_content || '',
              execution_monitoring: prog.execution_monitoring || '',
              certification_modalities: prog.certification_modalities || '',
              pricing_enterprise: prog.pricing_enterprise || 0,
              pricing_individual: prog.pricing_individual || 0,
              pricing_independent: prog.pricing_independent || 0,
            }));
          }
        }
      } catch (e) { /* silent */ }
      setLoading(false);
    };
    load();
  }, [sessionId, programmeId, isEditMode]);

  // Fetch invoices when session is loaded in edit mode
  useEffect(() => {
    if (isEditMode && sessionId) fetchFactures();
  }, [isEditMode, sessionId, fetchFactures]);

  // ─── Documents & E-learning fetch functions ───
  const fetchDocFolders = useCallback(async () => {
    if (!sessionId) return;
    try {
      const res = await fetch(`${API}/admin/sessions/${sessionId}/folders`, { credentials: 'include' });
      if (res.ok) setDocFolders(await res.json());
    } catch (e) { /* silent */ }
  }, [sessionId]);

  const fetchDocFiles = useCallback(async () => {
    if (!sessionId) return;
    try {
      const res = await fetch(`${API}/admin/sessions/${sessionId}/documents`, { credentials: 'include' });
      if (res.ok) setDocFiles(await res.json());
    } catch (e) { /* silent */ }
  }, [sessionId]);

  const fetchElSequences = useCallback(async () => {
    if (!sessionId) return;
    try {
      const res = await fetch(`${API}/admin/sessions/${sessionId}/elearning/sequences`, { credentials: 'include' });
      if (res.ok) setElSequences(await res.json());
    } catch (e) { /* silent */ }
  }, [sessionId]);

  const fetchElParcours = useCallback(async () => {
    if (!sessionId) return;
    try {
      const res = await fetch(`${API}/admin/sessions/${sessionId}/elearning/parcours`, { credentials: 'include' });
      if (res.ok) setElParcours(await res.json());
    } catch (e) { /* silent */ }
  }, [sessionId]);

  useEffect(() => {
    if (isEditMode && sessionId) {
      fetchDocFolders();
      fetchDocFiles();
      fetchElSequences();
      fetchElParcours();
    }
  }, [isEditMode, sessionId, fetchDocFolders, fetchDocFiles, fetchElSequences, fetchElParcours]);

  // Document actions
  const handleUploadDoc = async (file) => {
    if (!sessionId || !file) return;
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('folder_id', selectedFolder);
      fd.append('shared_with', 'all');
      const res = await fetch(`${API}/admin/sessions/${sessionId}/documents`, { method: 'POST', credentials: 'include', body: fd });
      if (res.ok) { toast.success('Document ajouté'); fetchDocFiles(); }
      else toast.error('Erreur d\'upload');
    } catch (e) { toast.error('Erreur d\'upload'); }
    setUploadingDoc(false);
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try {
      const res = await fetch(`${API}/admin/sessions/${sessionId}/documents/${docId}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { toast.success('Document supprimé'); fetchDocFiles(); }
    } catch (e) { toast.error('Erreur'); }
  };

  const handleShareDoc = async () => {
    if (!shareDocId) return;
    try {
      const res = await fetch(`${API}/admin/sessions/${sessionId}/documents/${shareDocId}/share`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shared_with: shareWith, shared_users: shareUsers }),
      });
      if (res.ok) { toast.success('Partage mis à jour'); setShowShareModal(false); fetchDocFiles(); }
    } catch (e) { toast.error('Erreur'); }
  };

  const handleCreateFolder = async () => {
    const n = window.prompt('Nom du dossier :');
    if (!n) return;
    try {
      const res = await fetch(`${API}/admin/sessions/${sessionId}/folders`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: n }),
      });
      if (res.ok) { toast.success('Dossier créé'); fetchDocFolders(); }
    } catch (e) { toast.error('Erreur'); }
  };

  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm('Supprimer ce dossier ? Les documents seront déplacés.')) return;
    try {
      const res = await fetch(`${API}/admin/sessions/${sessionId}/folders/${folderId}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { toast.success('Dossier supprimé'); fetchDocFolders(); fetchDocFiles(); }
    } catch (e) { toast.error('Erreur'); }
  };

  // E-learning actions
  const handleCreateSequence = async (title) => {
    try {
      const res = await fetch(`${API}/admin/sessions/${sessionId}/elearning/sequences`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, parcours_id: elSelectedParcours }),
      });
      if (res.ok) { toast.success('Séquence créée'); fetchElSequences(); const data = await res.json(); setShowSequenceEditor(data); setShowAddSequence(false); }
    } catch (e) { toast.error('Erreur'); }
  };

  const handleUpdateSequence = async (seqId, updates) => {
    try {
      const res = await fetch(`${API}/admin/sessions/${sessionId}/elearning/sequences/${seqId}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      if (res.ok) { toast.success('Séquence mise à jour'); fetchElSequences(); const data = await res.json(); setShowSequenceEditor(data); }
    } catch (e) { toast.error('Erreur'); }
  };

  const handleDeleteSequence = async (seqId) => {
    if (!window.confirm('Supprimer cette séquence ?')) return;
    try {
      const res = await fetch(`${API}/admin/sessions/${sessionId}/elearning/sequences/${seqId}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { toast.success('Séquence supprimée'); fetchElSequences(); setShowSequenceEditor(null); }
    } catch (e) { toast.error('Erreur'); }
  };

  const handleAddActivity = async (seqId, activity) => {
    try {
      const res = await fetch(`${API}/admin/sessions/${sessionId}/elearning/sequences/${seqId}/activities`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(activity),
      });
      if (res.ok) { toast.success('Activité ajoutée'); fetchElSequences(); }
    } catch (e) { toast.error('Erreur'); }
  };

  const handleCreateParcours = async () => {
    const n = window.prompt('Nom du parcours :');
    if (!n) return;
    try {
      const res = await fetch(`${API}/admin/sessions/${sessionId}/elearning/parcours`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: n }),
      });
      if (res.ok) { toast.success('Parcours créé'); fetchElParcours(); }
    } catch (e) { toast.error('Erreur'); }
  };

  const handleDeleteParcours = async (parcoursId) => {
    if (!window.confirm('Supprimer ce parcours ?')) return;
    try {
      const res = await fetch(`${API}/admin/sessions/${sessionId}/elearning/parcours/${parcoursId}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { toast.success('Parcours supprimé'); fetchElParcours(); fetchElSequences(); }
    } catch (e) { toast.error('Erreur'); }
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Le nom de la session est requis'); return; }
    setSaving(true);
    try {
      const url = isEditMode ? `${API}/admin/sessions/${sessionId}` : `${API}/admin/sessions`;
      const method = isEditMode ? 'PUT' : 'POST';
      const payload = { ...form, date: form.start_date || form.date };
      const res = await fetch(url, {
        method, credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success(isEditMode ? 'Session mise à jour' : 'Session créée avec succès');
        if (!isEditMode) {
          const data = await res.json();
          navigate(`/sessions/${data.session_id}/edit`);
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur lors de la sauvegarde');
      }
    } catch (e) {
      toast.error('Erreur de connexion');
    }
    setSaving(false);
  };

  const handleClone = async () => {
    if (!isEditMode) return;
    try {
      const res = await fetch(`${API}/admin/sessions/${sessionId}/clone`, {
        method: 'POST', credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        toast.success('Session clonée');
        navigate(`/sessions/${data.session_id}/edit`);
      }
    } catch (e) { toast.error('Erreur'); }
  };

  const handleArchive = async () => {
    if (!isEditMode) return;
    try {
      await fetch(`${API}/admin/sessions/${sessionId}/archive`, {
        method: 'PUT', credentials: 'include',
      });
      toast.success('Session archivée');
      navigate('/sessions');
    } catch (e) { toast.error('Erreur'); }
  };

  const handleDelete = async () => {
    if (!isEditMode) return;
    if (!window.confirm('Supprimer cette session ?')) return;
    try {
      await fetch(`${API}/admin/sessions/${sessionId}`, {
        method: 'DELETE', credentials: 'include',
      });
      toast.success('Session supprimée');
      navigate('/sessions');
    } catch (e) { toast.error('Erreur'); }
  };

  // Creneaux management
  const addCreneau = () => update('creneaux', [...form.creneaux, { ...EMPTY_CRENEAU }]);
  const removeCreneau = (i) => update('creneaux', form.creneaux.filter((_, idx) => idx !== i));
  const updateCreneau = (i, field, value) => {
    const updated = [...form.creneaux];
    updated[i] = { ...updated[i], [field]: value };
    update('creneaux', updated);
  };

  // Module management
  const [editingModule, setEditingModule] = useState(null);
  const [moduleForm, setModuleForm] = useState({ title: '', prix_client: 0, modalite: 'distanciel', hours: 0, start_date: '', end_date: '', creneaux_count: 0, dates_count: 0 });
  const [expandedModules, setExpandedModules] = useState({});
  const [programmeDetailsOpen, setProgrammeDetailsOpen] = useState(true);
  const [expandedApprenants, setExpandedApprenants] = useState({});

  const addModule = () => {
    if (!moduleForm.title.trim()) { toast.error('Le titre du module est requis'); return; }
    const mod = { ...moduleForm, module_id: `mod_${Date.now()}` };
    update('modules', [...form.modules, mod]);
    setModuleForm({ title: '', prix_client: 0, modalite: 'distanciel', hours: 0, start_date: '', end_date: '', creneaux_count: 0, dates_count: 0 });
    setEditingModule(null);
  };
  const removeModule = (i) => update('modules', form.modules.filter((_, idx) => idx !== i));
  const toggleModuleExpand = (i) => setExpandedModules(prev => ({ ...prev, [i]: !prev[i] }));

  // Client management
  const addClient = (type) => {
    const name = window.prompt(type === 'entreprise' ? "Nom de l'entreprise :" : 'Nom du client particulier :');
    if (!name) return;
    const client = { client_id: `cli_${Date.now()}`, name, type, cost: form.pricing_enterprise || 0, status: 'en_cours', modules: form.modules.map(m => m.title) };
    update('clients', [...(form.clients || []), client]);
  };

  const formatPrice = (v) => parseFloat(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 }) + ' €';

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-teal-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4" data-testid="session-create-page">
        {/* Breadcrumb */}
        <div className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1">
          <button onClick={() => navigate('/sessions')} className="hover:text-teal-600 flex items-center gap-1" data-testid="breadcrumb-sessions">
            <ArrowLeft className="h-3 w-3" /> Toutes mes sessions
          </button>
          <ChevronRight className="h-3 w-3" />
          <span className="text-teal-600 font-medium truncate max-w-[300px]">{form.title || 'Nouvelle session'}</span>
        </div>

        {/* Programme info banner */}
        {programme && (
          <Card className="border-teal-200 bg-teal-50/50 dark:bg-teal-900/20 dark:border-teal-800">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-teal-500 flex items-center justify-center shrink-0">
                <GraduationCap className="h-4 w-4 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-teal-600 dark:text-teal-400 font-medium">Programme :</p>
                <p className="text-sm text-slate-900 dark:text-white font-semibold truncate">{programme.title}</p>
              </div>
              {programme.duration_hours > 0 && (
                <Badge variant="outline" className="text-xs shrink-0"><Clock className="h-3 w-3 mr-1" />{programme.duration_hours}h</Badge>
              )}
            </CardContent>
          </Card>
        )}

        {/* ─── Primary stepper tabs ─── */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6" data-testid="primary-tabs">
          <div className="flex items-start justify-between relative">
            {/* Connecting line */}
            <div className="absolute top-7 left-[10%] right-[10%] h-0.5 bg-slate-200 dark:bg-slate-600" />
            <div
              className="absolute top-7 left-[10%] h-0.5 bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-500"
              style={{ width: `${(PRIMARY_TABS.findIndex(t => t.id === primaryTab) / (PRIMARY_TABS.length - 1)) * 80}%` }}
            />
            {PRIMARY_TABS.map((tab) => {
              const Icon = tab.icon;
              const active = primaryTab === tab.id;
              const idx = PRIMARY_TABS.findIndex(t => t.id === tab.id);
              const activeIdx = PRIMARY_TABS.findIndex(t => t.id === primaryTab);
              const completed = idx < activeIdx;
              return (
                <button
                  key={tab.id}
                  onClick={() => setPrimaryTab(tab.id)}
                  data-testid={`tab-${tab.id}`}
                  className="flex flex-col items-center gap-2 relative z-10 group w-1/5"
                >
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 ${
                    active
                      ? `bg-gradient-to-br ${tab.color} text-white shadow-lg ring-4 ${tab.ring} scale-110`
                      : completed
                        ? `bg-gradient-to-br ${tab.color} text-white shadow-md`
                        : `${tab.bg} ${tab.text} group-hover:shadow-md`
                  }`}>
                    <Icon className={`${active ? 'h-6 w-6' : 'h-5 w-5'}`} />
                  </div>
                  <span className={`text-xs font-medium text-center transition-colors ${
                    active ? tab.text + ' font-bold' : 'text-slate-500 dark:text-slate-400'
                  }`}>
                    {tab.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ─── Action buttons bar ─── */}
        <div className="flex items-center gap-2 flex-wrap" data-testid="action-buttons">
          <Button className="bg-purple-600 hover:bg-purple-700 text-white" onClick={handleSave} disabled={saving} data-testid="save-session-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Enregistrer
          </Button>
          {isEditMode && (
            <>
              <Button variant="outline" size="sm" className="border-purple-200 text-purple-700 hover:bg-purple-50" data-testid="inscription-btn"><UserPlus className="h-4 w-4 mr-1" />Inscription</Button>
              <Button variant="outline" size="sm" onClick={handleClone} data-testid="clone-session-btn"><Copy className="h-4 w-4 mr-1" />Cloner</Button>
              <Button variant="outline" size="sm" onClick={handleArchive} data-testid="archive-session-btn"><Archive className="h-4 w-4 mr-1" />Archiver</Button>
              <Button variant="outline" size="sm" data-testid="espace-entreprise-btn"><Building2 className="h-4 w-4 mr-1" />Espace Entreprise</Button>
              <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={handleDelete} data-testid="delete-session-btn">
                <Trash2 className="h-4 w-4 mr-1" />Supprimer
              </Button>
            </>
          )}
        </div>

        {/* ─── Tab Content ─── */}
        {primaryTab === 'configuration' && (
          <SessionConfigTab
            form={form} update={update}
            intervenantsList={intervenantsList} apprenantsList={apprenantsList}
            lieuxList={lieuxList} programme={programme} setProgramme={setProgramme}
            moduleForm={moduleForm} setModuleForm={setModuleForm}
            addModule={addModule} removeModule={removeModule}
            expandedModules={expandedModules} toggleModuleExpand={toggleModuleExpand}
            programmeDetailsOpen={programmeDetailsOpen} setProgrammeDetailsOpen={setProgrammeDetailsOpen}
            addClient={addClient} addCreneau={addCreneau} removeCreneau={removeCreneau} updateCreneau={updateCreneau}
            formatPrice={formatPrice}
          />
        )}

        {primaryTab === 'avancement' && (
          <SessionAvancementTab sessionId={sessionId} form={form} />
        )}

        {primaryTab === 'gestion' && (
          <SessionGestionTab
            form={form} update={update} sessionId={sessionId}
            formatPrice={formatPrice}
            facturesList={facturesList} deleteFacture={deleteFacture}
            openInvoiceModal={openInvoiceModal}
            showInvoiceModal={showInvoiceModal} invoiceClient={invoiceClient}
            setShowInvoiceModal={setShowInvoiceModal} fetchFactures={fetchFactures}
          />
        )}

        {primaryTab === 'espace_apprenant' && (
          <SessionEspaceApprenantTab
            form={form} update={update} sessionId={sessionId}
            docFolders={docFolders} docFiles={docFiles}
            selectedFolder={selectedFolder} setSelectedFolder={setSelectedFolder}
            showShareModal={showShareModal} setShowShareModal={setShowShareModal}
            shareDocId={shareDocId} setShareDocId={setShareDocId}
            shareSearch={shareSearch} setShareSearch={setShareSearch}
            shareWith={shareWith} setShareWith={setShareWith}
            shareUsers={shareUsers} setShareUsers={setShareUsers}
            uploadingDoc={uploadingDoc} handleUploadDoc={handleUploadDoc}
            handleDeleteDoc={handleDeleteDoc} handleShareDoc={handleShareDoc}
            handleCreateFolder={handleCreateFolder} handleDeleteFolder={handleDeleteFolder}
            elSequences={elSequences} elParcours={elParcours}
            elSelectedParcours={elSelectedParcours} setElSelectedParcours={setElSelectedParcours}
            showSequenceEditor={showSequenceEditor} setShowSequenceEditor={setShowSequenceEditor}
            showAddSequence={showAddSequence} setShowAddSequence={setShowAddSequence}
            handleCreateSequence={handleCreateSequence} handleUpdateSequence={handleUpdateSequence}
            handleDeleteSequence={handleDeleteSequence} handleAddActivity={handleAddActivity}
            handleCreateParcours={handleCreateParcours} handleDeleteParcours={handleDeleteParcours}
            fetchElSequences={fetchElSequences}
          />
        )}

        {primaryTab === 'suivi' && (
          <SessionSuiviTab form={form} sessionId={sessionId} />
        )}
      </div>
    </Layout>
  );
};

export default SessionCreatePage;
